import React, { useEffect, useRef, useState } from "react";
import type { InstagramFeedBlock } from "@/types/content";
import type { InstagramPost } from "@/api/instagram";
import { fetchInstagramPosts } from "@/api/instagram";
import styles from "./InstagramFeed.module.css";

export interface InstagramFeedProps {
  block: InstagramFeedBlock;
}

export const InstagramFeed: React.FC<InstagramFeedProps> = ({ block }) => {
  const [posts, setPosts] = useState<InstagramPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController>();

  const count = block.postCount && block.postCount > 0 ? block.postCount : 6;

  useEffect(() => {
    if (!block.instagramAccountId) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPosts(null);
    setError(null);

    fetchInstagramPosts(block.instagramAccountId, count, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.ok) {
          setPosts(result.data);
        } else {
          setError(result.error);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setError("Failed to load Instagram posts");
        }
      });

    return () => controller.abort();
  }, [block.instagramAccountId, count]);

  if (!block.instagramAccountId) {
    return null;
  }

  return (
    <section className={styles.container} data-component="instagram-feed">
      {block.heading && <h2 className={styles.heading}>{block.heading}</h2>}

      {/* Loading state */}
      {posts === null && error === null && (
        <div className={styles.grid}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={styles.skeleton} aria-hidden="true" />
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Instagram feed unavailable</p>
          <button
            className={styles.retryButton}
            onClick={() => {
              setError(null);
              setPosts(null);
              fetchInstagramPosts(block.instagramAccountId, count).then(
                (result) => {
                  if (result.ok) {
                    setPosts(result.data);
                  } else {
                    setError(result.error);
                  }
                }
              );
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {posts !== null && posts.length === 0 && (
        <p className={styles.emptyText}>No posts available</p>
      )}

      {/* Success state */}
      {posts !== null && posts.length > 0 && (
        <div className={styles.grid}>
          {posts.map((post) => (
            <a
              key={post.id}
              href={post.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.postLink}
              aria-label={
                post.caption
                  ? `Instagram post: ${post.caption.slice(0, 80)}`
                  : "Instagram post"
              }
            >
              <img
                src={
                  post.mediaType === "VIDEO" && post.thumbnailUrl
                    ? post.thumbnailUrl
                    : post.mediaUrl
                }
                alt={post.caption?.slice(0, 120) || "Instagram post"}
                className={styles.postImage}
                loading="lazy"
              />
              {post.mediaType === "VIDEO" && (
                <span className={styles.playIcon} aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              )}
              {post.caption && (
                <div className={styles.overlay}>
                  <p className={styles.caption}>
                    {post.caption.length > 80
                      ? `${post.caption.slice(0, 80)}...`
                      : post.caption}
                  </p>
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </section>
  );
};

export default InstagramFeed;
